import { observer } from "mobx-react-lite";

import {
  isNestedFilterType,
  usesStatusValuePresentation,
} from "../config/filter-value-type-behavior";
import { MAX_VISIBLE_FILTER_MULTI_VALUES } from "../config/constants";
import { getFilterDefinitionByKey } from "../config/structured-search-schema";
import { useStructuredSearchStore } from "../stores";
import AutocompleteDropdown from "../AutocompleteDropdown";

import FieldPill from "./FieldPill";
import KeyPill from "./KeyPill";
import MultiValueBadges from "./MultiValueBadges";
import OperatorPill from "./OperatorPill";
import ValueInputRouter from "./ValueInputRouter";
import { useFilterBuilderActions } from "./hooks/useFilterBuilderActions";
import { useFilterBuilderEditor } from "./hooks/useFilterBuilderEditor";
import { useFilterBuilderValueState } from "./hooks/useFilterBuilderValueState";

interface FilterBuilderRowProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  placeholder: string;
  isEditMode?: boolean;
  showAutocompleteDropdown?: boolean;
  onStructuredKeyDown: (event: React.KeyboardEvent) => void;
}

const FilterBuilderRow = observer(
  ({
    inputRef,
    placeholder,
    isEditMode = false,
    showAutocompleteDropdown = true,
    onStructuredKeyDown,
  }: FilterBuilderRowProps) => {
    const store = useStructuredSearchStore();
    const isMultiSelectValueStep = store.step === "value" && store.isMultiSelectMode;
    const containerClassName = isEditMode
      ? "relative flex min-w-0 flex-1 flex-wrap items-center gap-2.5"
      : "relative flex min-w-0 flex-1 flex-wrap items-center gap-2.5";
    const {
      canApplyDateValue,
      datePickerRange,
      handleApplyDatePicker,
      handleDatePickerChange,
      inputMode,
      inputValue,
      isDateInput,
      isDateRangeInput,
      operatorNeedsValue,
      shouldShowValueDropdown,
    } = useFilterBuilderValueState(store);
    const {
      focusInput,
      handleBlur,
      handleEditorBlurCapture,
      handleEditorFocusCapture,
      handleFocus,
      isEditorActive,
    } = useFilterBuilderEditor({
      store,
      inputRef,
      isEditMode,
      isDateInput,
      isDateRangeInput,
      shouldShowValueDropdown,
    });
    const {
      handleChange,
      handleFieldPillClick,
      handleInputKeyDown,
      handleKeyPillClick,
      handleOperatorPillClick,
      textPlaceholder,
    } = useFilterBuilderActions({
      store,
      focusInput,
      placeholder,
      onStructuredKeyDown,
    });

    const filterDefinition = getFilterDefinitionByKey(store.schema, store.inProgress.field);
    const usesStatusPresentation = usesStatusValuePresentation(filterDefinition);
    const multiValueBadgeClassName = usesStatusPresentation
      ? "shadow-none"
      : "border-slate-200 from-slate-50 to-slate-50 text-slate-700 shadow-none";

    return (
      <div
        className={containerClassName}
        onFocusCapture={handleEditorFocusCapture}
        onBlurCapture={handleEditorBlurCapture}
      >
        {(store.step === "operator" || store.step === "key") && store.inProgress.field && (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <FieldPill field={store.inProgress.field} onClick={handleFieldPillClick} />
            {store.inProgress.type &&
              isNestedFilterType(store.inProgress.type) &&
              store.inProgress.nestedKey && (
                <KeyPill
                  keyName={store.inProgress.nestedKeyDisplay ?? store.inProgress.nestedKey}
                  onClick={handleKeyPillClick}
                />
              )}
          </div>
        )}
        {store.step === "value" && operatorNeedsValue && !store.isMultiSelectMode && (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <FieldPill field={store.inProgress.field} onClick={handleFieldPillClick} />
            {store.inProgress.type &&
              isNestedFilterType(store.inProgress.type) &&
              store.inProgress.nestedKey && (
                <KeyPill
                  keyName={store.inProgress.nestedKeyDisplay ?? store.inProgress.nestedKey}
                  onClick={handleKeyPillClick}
                />
              )}
            <OperatorPill
              operator={store.inProgress.operator}
              field={store.inProgress.field}
              nestedKey={store.inProgress.nestedKey}
              onClick={handleOperatorPillClick}
            />
          </div>
        )}
        {isMultiSelectValueStep && (
          <div className="flex min-w-0 max-w-full flex-1 flex-nowrap items-center gap-2">
            <FieldPill field={store.inProgress.field} onClick={handleFieldPillClick} />
            {store.inProgress.type &&
              isNestedFilterType(store.inProgress.type) &&
              store.inProgress.nestedKey && (
                <KeyPill
                  keyName={store.inProgress.nestedKeyDisplay ?? store.inProgress.nestedKey}
                  onClick={handleKeyPillClick}
                />
              )}
            <OperatorPill
              operator={store.inProgress.operator}
              field={store.inProgress.field}
              nestedKey={store.inProgress.nestedKey}
              onClick={handleOperatorPillClick}
            />
            {usesStatusPresentation && <span className="shrink-0 text-sm text-slate-400">(</span>}
            {store.multiValues.length > 0 && (
              <div className="min-w-0 flex-1 overflow-hidden">
                <MultiValueBadges
                  layout="inline"
                  maxVisibleItems={MAX_VISIBLE_FILTER_MULTI_VALUES}
                  values={store.multiValues}
                  displayValues={store.multiValueDisplay}
                  definition={filterDefinition}
                  onRemove={value => store.removeMultiValue(value)}
                  badgeClassName={multiValueBadgeClassName}
                  textClassName="min-w-0 max-w-[140px] truncate"
                />
              </div>
            )}
            {usesStatusPresentation && <span className="shrink-0 text-sm text-slate-400">)</span>}
            <div
              className={
                store.multiValues.length > 0
                  ? "mr-2 flex w-24 min-w-0 max-w-32 shrink flex-col gap-1"
                  : "mr-2 flex min-w-[6rem] flex-1 flex-col gap-1"
              }
            >
              <ValueInputRouter
                mode={inputMode}
                inputRef={inputRef}
                value={inputValue}
                textPlaceholder={textPlaceholder}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleInputKeyDown}
                dateValue={datePickerRange}
                onDateChange={handleDatePickerChange}
                onDateApply={handleApplyDatePicker}
                canApplyDateValue={canApplyDateValue}
              />
            </div>
          </div>
        )}
        {!isMultiSelectValueStep && (
          <div className="mr-2 flex min-w-0 flex-1 flex-col gap-1">
            <ValueInputRouter
              mode={inputMode}
              inputRef={inputRef}
              value={inputValue}
              textPlaceholder={textPlaceholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleInputKeyDown}
              dateValue={datePickerRange}
              onDateChange={handleDatePickerChange}
              onDateApply={handleApplyDatePicker}
              canApplyDateValue={canApplyDateValue}
            />
          </div>
        )}
        {showAutocompleteDropdown && !isDateInput && !isDateRangeInput && (
          <AutocompleteDropdown isEditorActive={isEditorActive} />
        )}
      </div>
    );
  },
);

export default FilterBuilderRow;
